import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  ArrowRightOnRectangleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const Withdrawal = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1); // 1: 確認, 2: 理由選択, 3: 最終確認
  const [reason, setReason] = useState('');
  const [otherReason, setOtherReason] = useState('');
  const [feedback, setFeedback] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const withdrawalReasons = [
    {
      value: 'no_longer_needed',
      label: 'サービスを利用する必要がなくなった',
      description: '就職・転職により単発バイトが不要になった'
    },
    {
      value: 'poor_user_experience',
      label: 'アプリの使いづらさ',
      description: 'UI/UXに問題がある、操作が複雑'
    },
    {
      value: 'insufficient_jobs',
      label: '求人数・質が不十分',
      description: '希望する求人が少ない、条件に合わない'
    },
    {
      value: 'payment_issues',
      label: '報酬・支払いに関する不満',
      description: '支払いの遅れ、手数料の高さなど'
    },
    {
      value: 'customer_support',
      label: 'カスタマーサポートの対応',
      description: 'サポートの質や対応速度に不満'
    },
    {
      value: 'privacy_concerns',
      label: 'プライバシー・セキュリティへの不安',
      description: '個人情報の取り扱いに関する懸念'
    },
    {
      value: 'better_alternative',
      label: '他のサービスに移行',
      description: 'より良い条件の他のプラットフォームを見つけた'
    },
    {
      value: 'other',
      label: 'その他',
      description: '上記以外の理由'
    }
  ];

  const handleWithdrawal = async () => {
    if (!confirmPassword) {
      toast.error('パスワードを入力してください');
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const client = apiWithAuth(token);
      const payload = {
        password: confirmPassword,
        reason: reason || undefined,
        other_reason: otherReason || undefined,
        feedback: feedback || undefined,
      };

      // 正式エンドポイント
      const candidates = ['/auth/delete-account'];

      let success = false;
      let lastErr = null;
      for (const path of candidates) {
        try {
          // axios.delete にボディを渡す場合は config.data に設定
          const res = await client.delete(path, { data: payload });
          if (res?.status >= 200 && res?.status < 300) {
            success = true;
            break;
          }
        } catch (e) {
          lastErr = e;
          // 404 の場合は次の候補へ、その他はそのまま継続して最終で報告
          const status = e?.response?.status;
          if (status === 404) continue;
          if (status === 401 || status === 403) {
            toast.error('認証エラーが発生しました。再ログイン後、もう一度お試しください。');
            setLoading(false);
            return;
          }
          if (status === 409) {
            toast.error('残高や進行中の申請があるため退会できません。まずはウォレットから引き出し/キャンセルしてください。');
            setLoading(false);
            return;
          }
        }
      }

      if (!success) {
        // バックエンド未実装/到達不可
        console.error('Account deletion endpoint not available', lastErr);
        toast.error('退会APIが現在利用できません。お手数ですがサポートまでお問い合わせください。');
        setLoading(false);
        return;
      }

      toast.success('退会処理が完了しました。ご利用ありがとうございました。');
      // ログアウト処理
      logout();
      navigate('/');
    } catch (error) {
      console.error('withdrawal failed:', error);
      const msg = error?.response?.data?.detail || '退会処理に失敗しました。カスタマーサポートにお問い合わせください。';
      toast.error(typeof msg === 'string' ? msg : '退会処理に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900 mb-2">退会前にご確認ください</h3>
            <ul className="text-red-800 text-sm space-y-1">
              <li> 退会すると、アカウントの復旧はできません</li>
              <li> 勤務履歴、評価、獲得バッジなどのデータはすべて削除されます</li>
              <li> 未引き出しのウォレット残高は失効します</li>
              <li> 進行中の求人応募は自動的にキャンセルされます</li>
              <li> 再度利用する場合は、新規登録が必要になります</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ウォレット残高チェック */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <div className="flex items-start">
          <InformationCircleIcon className="w-6 h-6 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-yellow-900 mb-2">ウォレット残高の確認</h3>
            <p className="text-yellow-800 text-sm mb-3">
              現在のウォレット残高: <strong>{user?.wallet_balance?.toLocaleString() || '0'}</strong>
            </p>
            {user?.wallet_balance > 0 && (
              <>
                <p className="text-yellow-800 text-sm mb-3">
                  退会前に必ずウォレット残高を引き出してください。退会後は引き出しできません。
                </p>
                <button
                  onClick={() => navigate('/wallet-management?tab=withdrawals')}
                  className="inline-flex items-center bg-blue-600 text-white px-3 py-2 rounded-md text-sm hover:bg-blue-700 transition-colors"
                >
                  ウォレットを引き出す
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => navigate('/mypage')}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          マイページに戻る
        </button>
        <button
          onClick={() => setStep(2)}
          className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          退会手続きを続ける
        </button>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">退会理由をお聞かせください</h3>
        <p className="text-gray-600 text-sm mb-6">
          サービス改善のため、退会理由を教えていただけますでしょうか。（任意）
        </p>
        
        <div className="space-y-3">
          {withdrawalReasons.map((reasonOption) => (
            <label
              key={reasonOption.value}
              className={`block p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                reason === reasonOption.value
                  ? 'border-red-500 bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <input
                type="radio"
                name="reason"
                value={reasonOption.value}
                checked={reason === reasonOption.value}
                onChange={(e) => setReason(e.target.value)}
                className="sr-only"
              />
              <div>
                <h4 className={`font-medium mb-1 ${
                  reason === reasonOption.value ? 'text-red-900' : 'text-gray-900'
                }`}>
                  {reasonOption.label}
                </h4>
                <p className={`text-sm ${
                  reason === reasonOption.value ? 'text-red-700' : 'text-gray-600'
                }`}>
                  {reasonOption.description}
                </p>
              </div>
            </label>
          ))}
        </div>

        {reason === 'other' && (
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              具体的な理由
            </label>
            <textarea
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="具体的な理由をお聞かせください"
            />
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          改善要望・ご意見（任意）
        </label>
        <textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          placeholder="サービス改善のためのご意見・ご要望があればお聞かせください"
        />
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => setStep(1)}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          戻る
        </button>
        <button
          onClick={() => setStep(3)}
          className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          次へ
        </button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <ExclamationTriangleIcon className="w-6 h-6 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-red-900 mb-2">最終確認</h3>
            <p className="text-red-800 text-sm mb-3">
              以下の内容で退会処理を実行します。この操作は取り消せません。
            </p>
            <div className="bg-white p-4 rounded border">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">アカウント:</span>
                  <span className="text-gray-900">{user?.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">ウォレット残高:</span>
                  <span className="text-gray-900">{user?.wallet_balance?.toLocaleString() || '0'}</span>
                </div>
                {reason && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">退会理由:</span>
                    <span className="text-gray-900">
                      {withdrawalReasons.find(r => r.value === reason)?.label || ''}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          パスワードを入力して本人確認 <span className="text-red-500">*</span>
        </label>
        <input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
          placeholder="現在のパスワードを入力してください"
          required
        />
      </div>

      <div className="flex space-x-4">
        <button
          onClick={() => setStep(2)}
          className="flex-1 py-3 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
        >
          戻る
        </button>
        <button
          onClick={handleWithdrawal}
          disabled={loading || !confirmPassword}
          className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              退会処理中...
            </>
          ) : (
            <>
              <ArrowRightOnRectangleIcon className="w-5 h-5 mr-2" />
              退会する
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">退会手続き</h1>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ステップインジケーター */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center space-x-4">
            {[1, 2, 3].map((stepNumber) => (
              <div key={stepNumber} className="flex items-center">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                  step >= stepNumber
                    ? 'bg-red-600 text-white'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {step > stepNumber ? (
                    <CheckCircleIcon className="w-5 h-5" />
                  ) : (
                    stepNumber
                  )}
                </div>
                {stepNumber < 3 && (
                  <div className={`w-16 h-1 mx-2 ${
                    step > stepNumber ? 'bg-red-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ステップタイトル */}
        <div className="text-center mb-8">
          <h2 className="text-lg font-semibold text-gray-900">
            {step === 1 && '退会に関する注意事項'}
            {step === 2 && '退会理由の選択'}
            {step === 3 && '最終確認'}
          </h2>
        </div>

        {/* ステップコンテンツ */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        {/* サポート情報 */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6">
          <div className="flex items-start">
            <InformationCircleIcon className="w-6 h-6 text-blue-600 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-medium text-blue-900 mb-2">退会に関するお問い合わせ</h4>
              <p className="text-blue-800 text-sm leading-relaxed mb-3">
                退会手続きでご不明な点がございましたら、カスタマーサポートまでお問い合わせください。
              </p>
              <button
                onClick={() => navigate('/mypage/contact')}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm"
              >
                お問い合わせする
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Withdrawal;